import * as yup from "yup";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline, IoAdd, IoTrashOutline } from "react-icons/io5";

import ReactSelectAsync from "./../../../components/common/ReactSelectAsync";
import Datepicker from "./../../../components/common/Datepicker";
import Textbox from "./../../../components/common/Textbox";
import Textarea from "./../../../components/common/Textarea";
import Toaster from "../../../components/common/Toaster";

import journalService from "../../../services/journalService";
import moment from "moment";

const schema = yup.object().shape({
  entries: yup.array().of(
    yup.object().shape({
      account_chart: yup.object().shape({
        label: yup.string().required("Please select account chart."),
        value: yup.string().required("Please select account chart."),
      }),
    })
  ),
});

const JournalForm = ({
  data,
  defaultEntries,
  isLoading,
  setLoading,
  onSuccess,
}) => {
  const [transactionDate, setTransactionDate] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const columns = [
    { title: "Account", width: "3/12" },
    { title: "Debit", width: "1/6" },
    { title: "Credit", width: "1/6" },
    { title: "Description", width: "1/6" },
    { title: "Name", width: "3/12" },
    { title: "", width: "1/12" },
  ];

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { entries: defaultEntries },
  });

  const {
    fields: entries,
    append,
    remove,
  } = useFieldArray({
    name: "entries",
    control,
  });

  useEffect(() => {
    if (data) {
      const transation_date = moment(data.date_value).format("MM-DD-yyyy");

      setValue("account_id", data.account_id);
      setValue("reference_id", data.reference_id);
      setValue("reference_type", data.reference);
      setValue("transaction_date", transation_date);
      setTransactionDate(transation_date);
    }
  }, [data, setValue]);

  const options = async (search, loadedOptions, { page }) => {
    const params = {
      type: "",
      search_query: search,
      current_page: page,
      size: 10,
      sort_column: { path: "created_on", order: "desc" },
    };

    const response = await journalService.getAccountChartsAsync(params);
    const { result: data, count, total_count } = response;

    const options = data.map((item) => {
      const container = {};
      container["label"] = item.name;
      container["value"] = item.id;

      return container;
    });

    const hasMore = total_count > count;

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      let debit = 0;
      let credit = 0;

      const entries = data.entries.map((item) => {
        const container = {};

        let amount = 0;
        let direction = 0;

        if (item.debit !== "") {
          debit += Number(item.debit);
          amount = item.debit;
          direction = 1;
        }

        if (item.credit !== "") {
          credit += Number(item.credit);
          amount = item.credit;
          direction = -1;
        }

        container["amount"] = amount;
        container["direction"] = direction;
        container["account_chart_id"] = item.account_chart.value;
        container["name"] = item.name;
        container["description"] = item.description;

        return container;
      });

      const item = { ...data };
      item["entries"] = entries;

      debit = debit.toFixed(2);
      credit = credit.toFixed(2);

      if (debit !== credit) {
        Toaster.Error(
          "Form cannot be processed. Debit and Credit must be equal."
        );

        setLoading(false);
        return;
      }

      await journalService.postUnpostedEntryAsync(item);

      Toaster.Success("Posted Successfully!");
      onSuccess(data);
      setLoading(false);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="w-32">
        <button className="overflow-hidden h-0 w-0" />
        <Datepicker
          id="transaction-date"
          name="transaction_date"
          title="Journal Date"
          dateFormat="MM-DD-yyyy"
          timeFormat={false}
          setValue={setValue}
          clearErrors={clearErrors}
          register={register("transaction_date")}
          onDateChange={setTransactionDate}
          value={transactionDate}
          initialValue={transactionDate}
          errors={errors}
        />
      </div>
      <table className="min-w-full table-fixed">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((item, index) => (
              <th
                key={index}
                className={`p-3 text-left first:rounded-l-lg last:rounded-r-lg text-gray-600 text-sm w-${item.width}`}
              >
                {item.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((item, index) => (
            <tr key={item.id} className="border-t first:border-t-0">
              <td className="p-4">
                <ReactSelectAsync
                  name={`entries[${index}].account_chart`}
                  options={options}
                  control={control}
                  errors={errors}
                  register={register(`entries[${index}].account_chart`)}
                />
                {errors &&
                  errors.entries &&
                  errors.entries[index] &&
                  errors.entries[index].account_chart.message && (
                    <span className="text-sm text-red-500">
                      Please select account chart
                    </span>
                  )}
              </td>
              <td className="p-4">
                <Textbox
                  name={`entries[${index}].debit`}
                  type="text"
                  register={register(`entries[${index}].debit`, {
                    onChange: (e) => setValue(`entries[${index}].credit`, ""),
                  })}
                  errors={errors}
                />
              </td>
              <td className="p-4">
                <Textbox
                  name={`entries[${index}].credit`}
                  type="text"
                  register={register(`entries[${index}].credit`, {
                    onChange: (e) => setValue(`entries[${index}].debit`, ""),
                  })}
                  errors={errors}
                />
              </td>
              <td className="p-4">
                <Textarea
                  name={`entries[${index}].description`}
                  errors={errors}
                  register={register(`entries[${index}].description`)}
                  className="resize-none form-control"
                  rows={1}
                />
              </td>
              <td className="p-4">
                <Textbox
                  name={`entries[${index}].name`}
                  type="text"
                  register={register(`entries[${index}].name`)}
                  errors={errors}
                />
              </td>
              <td>
                {index !== 0 && (
                  <IoTrashOutline
                    className="text-red-500 cursor-pointer"
                    onClick={() => remove(index)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-between px-2">
        <button
          type="button"
          className="text-sm flex items-center text-blue-500"
          onClick={() => {
            append({ account_chart: "" });
          }}
        >
          <IoAdd className="mr-2" />
          Add Row
        </button>
        <button
          type="submit"
          className="button button-primary w-52 disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
          {!isLoading && <IoSaveOutline className="mr-3" />}
          Save Changes
        </button>
      </div>
    </form>
  );
};

export default JournalForm;
